/**
 * DealCreationWizard - 3-step wizard for creating new deals
 *
 * Step 1: Customer (search-as-you-type / create with address autocomplete)
 * Step 2: Services + Intake Fields (tile selector + dynamic fields)
 * Step 3: Assessment (summary + always 2 buttons: Pre-Site / Quoting)
 *
 * Replaces the original NewDealModal with multi-service support.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useModalAccessibility } from '@/hooks/useModalAccessibility';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, UserPlus, ArrowRight, ArrowLeft, Loader2, CheckCircle2, MapPin, ClipboardList, Zap } from 'lucide-react';
import {
  DealCategory,
  DealStage,
  DEAL_CATEGORY_LABELS,
} from '@/types/theo-workflow.types';
import type { ServiceModuleData } from '@/types/theo-workflow.types';
import { ServiceTileSelector } from './ServiceTileSelector';
import { ServiceIntakeFields } from './ServiceIntakeFields';
import { AddressAutocomplete } from './AddressAutocomplete';
import type { AddressResult } from './AddressAutocomplete';
// Phase 0 (trade-pack architecture): doesServiceRequirePresite() reads the
// static SERVICE_CONFIGS map directly. Pack-introduced categories don't drive
// pre-site logic (Pro-tier-only legacy code path). Phase 1+ will replace this
// import with a pack-aware variant when pre-site flow becomes pack-driven.
import { doesServiceRequirePresite } from '@/data/service-configs';
import { useCustomerMutations } from '@/hooks/useCustomers';
import { useDealMutations } from '@/hooks/useDeals';
import { db } from '@/firebase';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import { makeCustomerMatcher } from '@/utils/customer-search';
import type { Customer, CustomerAddress } from '@/types';
import { createPresiteJobFromDeal } from '@/services/deal-workflow.service';
import { seedDealIntakeNotes } from '@/services/deal-intake-notes.service';
import { ContactRolePicker, type ContactRoleSelection } from '@/components/ContactRolePicker';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (dealId: string) => void;
  /** When provided, Step 1 opens with this customer already selected
   *  so the search flow is skipped. Used by quick-action launchers in
   *  CustomerDetailModal. */
  preSelectedCustomerId?: string;
  /** Optional starting text for the deal notes — used by Send-to-Deal
   *  conversions (fault / service request) to carry context across.
   *  Only applied when the notes field is still empty. */
  seedNotes?: string;
  /** Stacking override — pass e.g. 'z-[110]' when launching from a parent
   *  that sits above the default z-50 (ServiceRequestDetailModal is z-[101]).
   *  Mirrors QuickRaiseJobModal's prop. */
  zIndexClass?: string;
}

type Step = 1 | 2 | 3;

interface CustomerSearch {
  results: Customer[];
  loading: boolean;
  searched: boolean;
}

export function DealCreationWizard({ isOpen, onClose, onSuccess, preSelectedCustomerId, seedNotes, zIndexClass = 'z-50' }: Props) {
  const modalRef = useModalAccessibility({ isOpen, onClose });
  const [step, setStep] = useState<Step>(1);

  // Step 1 - Customer
  const [customerMode, setCustomerMode] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [customerSearch, setCustomerSearch] = useState<CustomerSearch>({ results: [], loading: false, searched: false });
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [selectedCustomerPhone, setSelectedCustomerPhone] = useState('');
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState('');
  const [selectedCustomerAddress, setSelectedCustomerAddress] = useState<{ address?: string; city?: string; state?: string; postcode?: string } | null>(null);
  const [savedCustomerAddresses, setSavedCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [newCustomer, setNewCustomer] = useState({
    name: '', phone: '', email: '', address: '',
    city: '', state: '', postcode: '', country: 'Australia',
  });

  // Step 2 - Services
  const [selectedServices, setSelectedServices] = useState<DealCategory[]>([]);
  const [moduleData, setModuleData] = useState<Partial<Record<DealCategory, ServiceModuleData>>>({});
  const [dealTitle, setDealTitle] = useState('');
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);

  // Step 3 - Assessment
  const [notes, setNotes] = useState('');
  const [contactSelection, setContactSelection] = useState<ContactRoleSelection>({});

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hooks
  const { createCustomer } = useCustomerMutations();
  const { createDeal } = useDealMutations();

  // Auto-generate deal title from customer name + services (unless manually edited)
  useEffect(() => {
    if (titleManuallyEdited || selectedServices.length === 0) return;
    const custName = customerMode === 'search' ? selectedCustomerName : newCustomer.name;
    const serviceLabels = selectedServices.map((s) => DEAL_CATEGORY_LABELS[s]).join(' + ');
    const autoTitle = custName
      ? `${custName} — ${serviceLabels}`
      : serviceLabels;
    setDealTitle(autoTitle);
  }, [selectedServices, customerMode, selectedCustomerName, newCustomer.name, titleManuallyEdited]);

  // Seed notes from a Send-to-Deal conversion (fault / service request).
  // Applied on open only when the notes field is still empty so a user's
  // in-progress edits are never clobbered.
  useEffect(() => {
    if (!isOpen || !seedNotes) return;
    setNotes((prev) => (prev.trim() ? prev : seedNotes));
     
  }, [isOpen, seedNotes]);

  // Pre-selection (V107.53) — when preSelectedCustomerId is provided,
  // fetch the customer doc and apply the same field hydration the
  // search-results onSelectCustomer path uses. Keeps the wizard in
  // 'search' mode (so the green selected-customer card renders) and
  // skips Step 1's empty state. Cancellable so a fast reopen with a
  // different id can't race a stale fetch.
  useEffect(() => {
    if (!isOpen || !preSelectedCustomerId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'customers', preSelectedCustomerId));
        if (cancelled || !snap.exists()) return;
        const c = { id: snap.id, ...snap.data() } as Customer;

        setCustomerMode('search');
        setSelectedCustomerId(c.id || preSelectedCustomerId);
        setSelectedCustomerName(c.name || '');
        setSelectedCustomerPhone(c.phone ?? '');
        setSelectedCustomerEmail(c.email ?? '');

        const addrs = c.addresses || [];
        setSavedCustomerAddresses(addrs);
        const primary = addrs.find((a) => a.isPrimary) || addrs[0];
        if (primary) {
          setSelectedCustomerAddress({ address: primary.address, city: primary.city, state: primary.state, postcode: primary.postcode });
        } else if (c.address || c.city || c.state) {
          setSelectedCustomerAddress({ address: c.address, city: c.city, state: c.state, postcode: c.postcode || c.zipCode });
        }
        // Populate the search box so the selected pill reads as "matched".
        setSearchQuery(c.name || '');
      } catch (err) {
        console.warn('[DealCreationWizard] preSelectedCustomerId fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, preSelectedCustomerId]);

  // Search-as-you-type debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setCustomerSearch({ results: [], loading: false, searched: false });
      return;
    }
    setCustomerSearch((prev) => ({ ...prev, loading: true }));

    try {
      // Fetch ALL customers and filter client-side for case-insensitive search
      // This ensures "john smith" matches "John Smith", "JOHN SMITH", etc.
      const fbQuery = query(
        collection(db, 'customers'),
        where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      );
      const snap = await getDocs(fbQuery);

      // Shared matcher → searches names, company, every phone/address,
      // account + external IDs (same fields as global search).
      const match = makeCustomerMatcher(q);
      const results = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as Customer))
        .filter(match);

      setCustomerSearch({ results, loading: false, searched: true });
    } catch {
      setCustomerSearch({ results: [], loading: false, searched: true });
    }
  }, []);

  useEffect(() => {
    if (customerMode !== 'search') return;
    // Skip the debounced search when a customer is already selected.
    // Pre-selection (Phase 5) populates searchQuery to make the search
    // field "look matched" — without this guard, the debounce would
    // then fire a needless lookup that surfaces a results dropdown the
    // user has to manually dismiss.
    if (selectedCustomerId) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      doSearch(searchQuery);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, customerMode, doSearch, selectedCustomerId]);

  // Computed
  const presiteServices = useMemo(() => {
    return selectedServices.filter((cat) => {
      const fields = (moduleData[cat]?.intakeFields as unknown as Record<string, unknown>) ?? {};
      return doesServiceRequirePresite(cat, fields);
    });
  }, [selectedServices, moduleData]);

  const canProceedStep1 = customerMode === 'search'
    ? !!selectedCustomerId
    : !!(newCustomer.name && newCustomer.phone);

  const canProceedStep2 = selectedServices.length > 0 && dealTitle.trim().length > 0;

  // Address autocomplete handler
  const handleAddressSelect = (result: AddressResult) => {
    setNewCustomer((prev) => ({
      ...prev,
      address: result.fullAddress || result.street,
      city: result.city,
      state: result.state,
      postcode: result.postcode,
      country: result.country || 'Australia',
    }));
  };

  // Field change handler
  const handleFieldChange = (category: DealCategory, fieldKey: string, value: unknown) => {
    setModuleData((prev) => {
      const existing = prev[category] ?? {
        presiteRequired: false,
        presiteCompleted: false,
        intakeFields: {} as Record<string, unknown>,
        presiteFields: {},
        postQuoteFields: {},
        provisioningFields: {},
        status: 'intake' as const,
      };

      return {
        ...prev,
        [category]: {
          ...existing,
          intakeFields: {
            ...(existing.intakeFields as unknown as Record<string, unknown>),
            [fieldKey]: value,
          },
        },
      };
    });
  };

  // Submit with explicit destination stage
  const handleSubmit = async (destination: 'presite' | 'quoting') => {
    setIsSubmitting(true);
    setError(null);

    let customerId = selectedCustomerId;
    let customerName = selectedCustomerName;
    let customerCreatedInThisAttempt = false;

    // STEP 1: Create customer FIRST (if needed) - this happens OUTSIDE the main try-catch
    // so that customers are ALWAYS created and NEVER rolled back
    if (customerMode === 'create') {
      try {
        setSubmitStep('Creating customer...');
        customerId = await createCustomer({
          name: newCustomer.name,
          phone: newCustomer.phone,
          email: newCustomer.email,
          address: newCustomer.address,
          city: newCustomer.city,
          state: newCustomer.state,
          zipCode: newCustomer.postcode,
        } as Parameters<typeof createCustomer>[0]);
        customerName = newCustomer.name;
        customerCreatedInThisAttempt = true;
        console.log(`✅ Customer created: ${customerId} (${customerName})`);
      } catch (custErr) {
        console.error('❌ Customer creation failed:', custErr);
        setError(custErr instanceof Error ? custErr.message : 'Failed to create customer');
        setIsSubmitting(false);
        setSubmitStep('');
        return; // Stop here if customer creation fails
      }
    }

    if (!customerId) {
      setError('No customer selected');
      setIsSubmitting(false);
      setSubmitStep('');
      return;
    }

    // STEP 2: Create deal and job - if this fails, customer still exists
    try {

      // Build service modules with presite flags
      const finalModules: Record<string, ServiceModuleData> = {};
      for (const cat of selectedServices) {
        const existing = moduleData[cat];
        const fields = (existing?.intakeFields as unknown as Record<string, unknown>) ?? {};
        const needsPresite = doesServiceRequirePresite(cat, fields);

        finalModules[cat] = {
          presiteRequired: destination === 'presite' ? true : needsPresite,
          presiteCompleted: false,
          intakeFields: existing?.intakeFields ?? ({} as Record<string, unknown>),
          presiteFields: {},
          postQuoteFields: {},
          provisioningFields: {},
          status: 'intake',
        } as ServiceModuleData;
      }

      // STEP 2A: Create deal
      setSubmitStep('Creating deal...');
      const userId = localStorage.getItem('user_id') ?? 'unknown';
      const userName = localStorage.getItem('logged_in_user') ?? 'Unknown';
      const initialStage = destination === 'presite' ? DealStage.PRESITE : DealStage.QUOTING;
      const now = new Date().toISOString();

      const isPresiteOverride = destination === 'quoting' && presiteServices.length > 0;

      const dealPayload: Parameters<typeof createDeal>[0] = {
        customerId,
        customerName,
        title: dealTitle,
        selectedServices,
        serviceModules: finalModules,
        presiteRequired: destination === 'presite',
        presiteOverridden: isPresiteOverride,
        notes,
        contactName: customerName,
        contactPhone: (customerMode === 'search' ? selectedCustomerPhone : newCustomer.phone) || undefined,
        contactEmail: (customerMode === 'search' ? selectedCustomerEmail : newCustomer.email) || undefined,
        jobAddress: (customerMode === 'search' ? selectedCustomerAddress?.address : newCustomer.address) || undefined,
        jobCity: (customerMode === 'search' ? selectedCustomerAddress?.city : newCustomer.city) || undefined,
        jobState: (customerMode === 'search' ? selectedCustomerAddress?.state : newCustomer.state) || undefined,
        jobZipCode: (customerMode === 'search' ? selectedCustomerAddress?.postcode : newCustomer.postcode) || undefined,
        stageHistory: [
          {
            stage: DealStage.INTAKE,
            enteredAt: now,
          },
          ...(destination === 'presite' ? [{
            stage: DealStage.PRESITE,
            enteredAt: now,
          }] : destination === 'quoting' ? [{
            stage: DealStage.QUOTING,
            enteredAt: now,
          }] : []),
        ],
        activityLog: [{
          id: crypto.randomUUID(),
          action: 'Deal created',
          details: `Services: ${selectedServices.map((s) => DEAL_CATEGORY_LABELS[s]).join(', ')}. Sent to ${destination === 'presite' ? 'Pre-Site' : 'Quoting'}.${isPresiteOverride ? ` Pre-site override by ${userName}.` : ''}`,
          actor: userId,
          actorName: userName,
          automated: false,
          timestamp: now,
        }],
        dealOwnerId: userId,
        dealOwnerName: userName,
        createdByName: userName,
        tenantId: getTenantIdSync(),
      };

      // Only include optional fields when they have values (Firestore rejects undefined)
      if (isPresiteOverride) dealPayload.presiteOverriddenBy = userName;
      if (newCustomer.address) dealPayload.jobAddress = newCustomer.address;
      if (newCustomer.city) dealPayload.jobCity = newCustomer.city;
      if (newCustomer.state) dealPayload.jobState = newCustomer.state;
      if (newCustomer.postcode) dealPayload.jobZipCode = newCustomer.postcode;

      // Contact split — only set when user actually picked someone (existing-customer path).
      if (contactSelection.accountContactId) {
        dealPayload.accountContactId = contactSelection.accountContactId;
        if (contactSelection.accountContactName) dealPayload.accountContactName = contactSelection.accountContactName;
        if (contactSelection.accountContactPhone) dealPayload.accountContactPhone = contactSelection.accountContactPhone;
        if (contactSelection.accountContactEmail) dealPayload.accountContactEmail = contactSelection.accountContactEmail;
      }
      if (contactSelection.siteContactId) {
        dealPayload.siteContactId = contactSelection.siteContactId;
        if (contactSelection.siteContactName) dealPayload.siteContactName = contactSelection.siteContactName;
        if (contactSelection.siteContactPhone) dealPayload.siteContactPhone = contactSelection.siteContactPhone;
        if (contactSelection.siteContactEmail) dealPayload.siteContactEmail = contactSelection.siteContactEmail;
      }

      // Primary contactEmail must always be set — Flodesk, invoice emails,
      // and proposal emails all read it. If a specific contact was picked,
      // prefer their email (site contact first — they experience the install
      // and receive most lifecycle comms; account contact as fallback).
      // Without this, ContactRolePicker selections wrote to accountContactEmail
      // / siteContactEmail but left contactEmail at the customer's primary,
      // meaning lifecycle comms went to the wrong person.
      if (contactSelection.siteContactEmail) {
        dealPayload.contactEmail = contactSelection.siteContactEmail;
      } else if (contactSelection.accountContactEmail) {
        dealPayload.contactEmail = contactSelection.accountContactEmail;
      }

      const dealId = await createDeal(dealPayload, userId, initialStage);
      console.log(`✅ Deal created: ${dealId}`);

      // Mirror per-service intake details (Surveillance, Starlink, etc. + the
      // per-service "Notes") into the unified notes collection so they surface
      // on the deal timeline, scheduler side panel, and the tech's field view —
      // not just the deal's collapsed "Intake Details". Fire-and-forget +
      // self-catching: never block deal creation on note seeding.
      void seedDealIntakeNotes(dealId, userId, userName);

      // STEP 2B: Create pre-site job if sending to pre-site stage
      if (destination === 'presite') {
        setSubmitStep('Creating pre-site job...');
        try {
          await createPresiteJobFromDeal(dealId, userId, userName);
          console.log(`✅ Pre-site job created for deal: ${dealId}`);
        } catch (jobErr) {
          console.error('❌ Failed to create pre-site job:', jobErr);

          // Rollback: Delete the deal that was just created
          setSubmitStep('Rolling back deal creation...');
          try {
            await deleteDoc(doc(db, 'deals', dealId));
            console.log(`⚠️ Deal ${dealId} rolled back due to job creation failure`);
          } catch (deleteErr) {
            console.error('❌ Failed to delete deal during rollback:', deleteErr);
          }

          // IMPORTANT: Customer is NOT rolled back - it remains in the database
          const customerPersistMsg = customerCreatedInThisAttempt
            ? ` Customer "${customerName}" (${customerId}) was created and saved.`
            : '';

          throw new Error(
            `Failed to create pre-site job: ${jobErr instanceof Error ? jobErr.message : 'Unknown error'}. ` +
            `Deal creation cancelled.${customerPersistMsg}`
          );
        }
      }

      setIsSubmitting(false);
      setSubmitStep('');
      resetForm();
      onSuccess?.(dealId);
      onClose();
    } catch (err) {
      console.error('❌ Error in deal creation workflow:', err);

      // Build error message with customer persistence info
      let errorMsg = err instanceof Error ? err.message : 'Failed to create deal';

      // If we created a customer in this attempt, inform the user it was saved
      if (customerCreatedInThisAttempt && !errorMsg.includes('Customer')) {
        errorMsg += ` Note: Customer "${customerName}" was successfully created and saved.`;
      }

      setError(errorMsg);
      setIsSubmitting(false);
      setSubmitStep('');

      // IMPORTANT: Don't reset form here - let user see what happened and decide what to do
    }
  };

  const resetForm = () => {
    setStep(1);
    setCustomerMode('search');
    setSearchQuery('');
    setCustomerSearch({ results: [], loading: false, searched: false });
    setSelectedCustomerId(null);
    setSelectedCustomerName('');
    setSelectedCustomerPhone('');
    setSelectedCustomerEmail('');
    setSelectedCustomerAddress(null);
    setNewCustomer({ name: '', phone: '', email: '', address: '', city: '', state: '', postcode: '', country: 'Australia' });
    setSelectedServices([]);
    setModuleData({});
    setDealTitle('');
    setTitleManuallyEdited(false);
    setNotes('');
    setContactSelection({});
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-surface-overlay backdrop-blur-sm p-4`} role="dialog" aria-modal="true" aria-label="New Deal">
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-surface-card border border-theme rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme">
          <div>
            <h2 className="text-lg font-bold text-theme-primary">New Deal</h2>
            <p className="text-xs text-theme-muted">
              Step {step} of 3 &mdash;{' '}
              {step === 1 ? 'Customer' : step === 2 ? 'Services & Details' : 'Assessment'}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-surface-raised rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-theme-muted" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-theme-soft">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-brand-secondary' : 'bg-surface-glass'
              }`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <StepContent key="step1">
                <Step1Customer
                  mode={customerMode}
                  setMode={setCustomerMode}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  customerSearch={customerSearch}
                  selectedCustomerId={selectedCustomerId}
                  onSelectCustomer={(id, name, customer) => {
                    setSelectedCustomerId(id);
                    setSelectedCustomerName(name);
                    setSelectedCustomerPhone(customer?.phone ?? '');
                    setSelectedCustomerEmail(customer?.email ?? '');

                    // Use multi-address if available, fall back to legacy single address
                    const addrs = customer?.addresses || [];
                    setSavedCustomerAddresses(addrs);
                    const primary = addrs.find((a) => a.isPrimary) || addrs[0];
                    if (primary) {
                      setSelectedCustomerAddress({ address: primary.address, city: primary.city, state: primary.state, postcode: primary.postcode });
                    } else {
                      setSelectedCustomerAddress(customer ? { address: customer.address, city: customer.city, state: customer.state, postcode: customer.postcode || customer.zipCode } : null);
                    }
                  }}
                  newCustomer={newCustomer}
                  setNewCustomer={setNewCustomer}
                  onAddressSelect={handleAddressSelect}
                  disabled={isSubmitting}
                />

                {/* Selected-customer confirmation — surfaces the pre-selected
                    customer (Send-to-deal from a fault / customer profile).
                    Without it, pre-selection filled the search box but gave no
                    visual proof anyone was actually attached to the deal. The
                    in-list gold pill covers picks made from live search
                    results, so this card only renders when the list is empty. */}
                {customerMode === 'search' && selectedCustomerId && customerSearch.results.length === 0 && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <p className="text-sm text-theme-primary min-w-0 truncate">
                      <span className="font-medium">Selected: {selectedCustomerName}</span>
                      {selectedCustomerPhone && (
                        <span className="text-theme-muted text-xs"> · {selectedCustomerPhone}</span>
                      )}
                    </p>
                  </div>
                )}

                {/* Address selector when customer has multiple saved addresses */}
                {customerMode === 'search' && selectedCustomerId && savedCustomerAddresses.length > 1 && (
                  <div className="mt-3">
                    <label className="text-xs text-theme-secondary mb-1.5 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Job Address
                    </label>
                    <select
                      className="w-full px-3 py-2.5 bg-surface-glass border border-theme rounded-lg text-sm text-theme-primary focus:outline-none focus:ring-2 focus:ring-brand-secondary/40"
                      value={selectedCustomerAddress?.address || ''}
                      onChange={(e) => {
                        const addr = savedCustomerAddresses.find((a) => a.address === e.target.value);
                        if (addr) {
                          setSelectedCustomerAddress({ address: addr.address, city: addr.city, state: addr.state, postcode: addr.postcode });
                        }
                      }}
                    >
                      {savedCustomerAddresses.map((a) => (
                        <option key={a.id} value={a.address}>
                          {a.label}: {a.address}{a.city ? `, ${a.city}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </StepContent>
            )}

            {step === 2 && (
              <StepContent key="step2">
                <div className="space-y-5">
                  <div>
                    <label className="text-xs text-theme-secondary mb-1.5 block">Deal Title *</label>
                    <input
                      type="text"
                      value={dealTitle}
                      onChange={(e) => {
                        setDealTitle(e.target.value);
                        setTitleManuallyEdited(true);
                      }}
                      placeholder="e.g., Smart Home + Security Install"
                      disabled={isSubmitting}
                      className="w-full px-3 py-2.5 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
                    />
                    {titleManuallyEdited && (
                      <button
                        type="button"
                        onClick={() => { setTitleManuallyEdited(false); }}
                        className="text-[10px] text-brand-secondary/60 hover:text-brand-secondary mt-1"
                      >
                        Reset to auto-generated title
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-theme-secondary mb-2 block">Select Services *</label>
                    <ServiceTileSelector
                      selectedServices={selectedServices}
                      onChange={setSelectedServices}
                      disabled={isSubmitting}
                    />
                  </div>

                  {selectedServices.length > 0 && (
                    <div className="pt-3 border-t border-theme-soft">
                      <ServiceIntakeFields
                        selectedServices={selectedServices}
                        moduleData={moduleData}
                        onChange={handleFieldChange}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}
                </div>
              </StepContent>
            )}

            {step === 3 && (
              <StepContent key="step3">
                <div className="space-y-5">
                  {/* Summary */}
                  <div className="bg-surface-glass border border-theme rounded-xl p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-theme-muted uppercase tracking-wider">Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-theme-secondary">Customer</span>
                        <span className="text-theme-primary font-medium">
                          {customerMode === 'search' ? selectedCustomerName : newCustomer.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-theme-secondary">Deal</span>
                        <span className="text-theme-primary font-medium">{dealTitle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-theme-secondary">Services</span>
                        <span className="text-theme-primary font-medium">
                          {selectedServices.map((s) => DEAL_CATEGORY_LABELS[s]).join(', ')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pre-site recommendation banners hidden — logic retained */}

                  {/* Contact split — existing customers only (new customers have no contacts yet) */}
                  {customerMode === 'search' && selectedCustomerId && (
                    <div className="bg-surface-glass border border-theme rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-xs font-semibold text-theme-muted uppercase tracking-wider">Deal Contacts</h4>
                          <p className="text-[11px] text-theme-muted mt-0.5">Defaulted to customer primaries — override if this deal needs different people.</p>
                        </div>
                      </div>
                      <ContactRolePicker
                        customerId={selectedCustomerId}
                        value={contactSelection}
                        onChange={setContactSelection}
                      />
                    </div>
                  )}

                  {/* Notes */}
                  <div>
                    <label className="text-xs text-theme-secondary mb-1.5 block">Call Notes (optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notes from the intake call..."
                      rows={3}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2.5 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 resize-none disabled:opacity-50"
                    />
                  </div>

                  {/* Two destination buttons - ALWAYS visible */}
                  <div className="pt-2 space-y-3">
                    <p className="text-xs text-theme-muted font-medium uppercase tracking-wider">Where should this deal go?</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleSubmit('presite')}
                        disabled={isSubmitting}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-amber-400/30 bg-amber-500/10 hover:bg-amber-500/20 hover:border-amber-400/50 transition-all disabled:opacity-50 group"
                      >
                        <ClipboardList className="w-6 h-6 text-amber-400 group-hover:scale-110 transition-transform" />
                        <span className="text-sm font-semibold text-amber-600 dark:text-amber-300">Send to Pre-Site</span>
                        <span className="text-[10px] text-theme-muted">Deploy for site assessment first</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSubmit('quoting')}
                        disabled={isSubmitting}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20 hover:border-emerald-400/50 transition-all disabled:opacity-50 group"
                      >
                        <Zap className="w-6 h-6 text-emerald-400 group-hover:scale-110 transition-transform" />
                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">Send to Quoting</span>
                        <span className="text-[10px] text-theme-muted">Skip pre-site, build quote now</span>
                      </button>
                    </div>
                  </div>
                </div>
              </StepContent>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-400/20 rounded-lg p-3">
              <p className="text-xs text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Submit step indicator */}
          {submitStep && (
            <div className="mt-4 bg-blue-500/10 border border-blue-400/20 rounded-lg p-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
              <p className="text-xs text-blue-600 dark:text-blue-300">{submitStep}</p>
            </div>
          )}
        </div>

        {/* Footer - only show nav for steps 1 and 2 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-theme">
          <button
            type="button"
            onClick={() => step > 1 ? setStep((step - 1) as Step) : handleClose()}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-sm text-theme-secondary hover:text-theme-primary transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 3 && (
            <button
              type="button"
              onClick={() => setStep((step + 1) as Step)}
              disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-secondary/20 border border-brand-secondary/30 rounded-xl text-sm font-medium text-brand-secondary hover:bg-brand-secondary/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {/* Step 3 uses the inline Pre-Site / Quoting buttons instead */}
          {step === 3 && <div />}
        </div>
      </motion.div>
    </div>
  );
}

// ============================================================================
// Step Content Wrapper
// ============================================================================

function StepContent({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      {children}
    </motion.div>
  );
}

// ============================================================================
// Step 1: Customer Search/Create
// ============================================================================

interface Step1Props {
  mode: 'search' | 'create';
  setMode: (m: 'search' | 'create') => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  customerSearch: CustomerSearch;
  selectedCustomerId: string | null;
  onSelectCustomer: (id: string, name: string, customer?: Customer) => void;
  newCustomer: { name: string; phone: string; email: string; address: string; city: string; state: string; postcode: string; country: string };
  setNewCustomer: (c: { name: string; phone: string; email: string; address: string; city: string; state: string; postcode: string; country: string }) => void;
  onAddressSelect: (result: AddressResult) => void;
  disabled?: boolean;
}

function Step1Customer({
  mode, setMode, searchQuery, setSearchQuery,
  customerSearch, selectedCustomerId, onSelectCustomer,
  newCustomer, setNewCustomer, onAddressSelect, disabled,
}: Step1Props) {
  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('search')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'search'
              ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/30'
              : 'bg-surface-glass text-theme-secondary border border-theme hover:border-theme'
          }`}
        >
          <Search className="w-4 h-4" />
          Search Existing
        </button>
        <button
          type="button"
          onClick={() => setMode('create')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'create'
              ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/30'
              : 'bg-surface-glass text-theme-secondary border border-theme hover:border-theme'
          }`}
        >
          <UserPlus className="w-4 h-4" />
          New Customer
        </button>
      </div>

      {mode === 'search' ? (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Start typing to search customers..."
              disabled={disabled}
              className="w-full pl-10 pr-4 py-2.5 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
            />
            {customerSearch.loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-muted animate-spin" />
            )}
          </div>

          {customerSearch.searched && !customerSearch.loading && customerSearch.results.length === 0 && searchQuery.trim().length >= 2 && (
            <p className="text-xs text-theme-muted text-center py-4">
              No customers found. Try a different search or create new.
            </p>
          )}

          {customerSearch.results.length > 0 && (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {customerSearch.results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelectCustomer(c.id!, c.name || '', c)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                    selectedCustomerId === c.id
                      ? 'bg-brand-secondary/20 border border-brand-secondary/30'
                      : 'bg-surface-glass border border-theme-soft hover:border-theme'
                  }`}
                >
                  <div>
                    <p className="text-sm text-theme-primary font-medium">{c.name}</p>
                    <p className="text-[11px] text-theme-muted">
                      {c.phone} {c.email && `· ${c.email}`}
                      {c.address && ` · ${c.address}`}
                    </p>
                  </div>
                  {selectedCustomerId === c.id && (
                    <CheckCircle2 className="w-4 h-4 text-brand-secondary shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-theme-secondary mb-1 block">Name *</label>
              <input
                type="text"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="John Smith"
                disabled={disabled}
                className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs text-theme-secondary mb-1 block">Phone *</label>
              <input
                type="tel"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="0400 000 000"
                disabled={disabled}
                className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs text-theme-secondary mb-1 block">Email</label>
              <input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="john@email.com"
                disabled={disabled}
                className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm placeholder-theme-muted focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Address with autocomplete */}
          <div>
            <label className="text-xs text-theme-secondary mb-1 block">
              <MapPin className="w-3 h-3 inline mr-1" />
              Address
            </label>
            <AddressAutocomplete
              value={newCustomer.address}
              onChange={(v) => setNewCustomer({ ...newCustomer, address: v })}
              onAddressSelect={onAddressSelect}
              disabled={disabled}
              placeholder="Start typing an address..."
            />
          </div>

          {/* Auto-populated address fields */}
          {(newCustomer.city || newCustomer.state || newCustomer.postcode) && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-theme-secondary mb-1 block">City</label>
                <input
                  type="text"
                  value={newCustomer.city}
                  onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                  disabled={disabled}
                  className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="text-xs text-theme-secondary mb-1 block">State</label>
                <input
                  type="text"
                  value={newCustomer.state}
                  onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                  disabled={disabled}
                  className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="text-xs text-theme-secondary mb-1 block">Postcode</label>
                <input
                  type="text"
                  value={newCustomer.postcode}
                  onChange={(e) => setNewCustomer({ ...newCustomer, postcode: e.target.value })}
                  disabled={disabled}
                  className="w-full px-3 py-2 bg-surface-glass border border-theme rounded-lg text-theme-primary text-sm focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
